import { useContext, useMemo } from 'react';

import {
  InsightsContentContext,
  InsightsEntityType,
} from 'contextProviders/InsightsContentProvider';
import { InsightsView } from 'contextProviders/InsightsContentProvider/viewTypes';
import useContentMetricIds, { ContentInput } from 'hooks/useContentMetricIds';
import useMetricOptions from 'hooks/useMetricOptions';

export type { InsightsEntityType };

export interface CurrentInsightsEntity {
  entityType: InsightsEntityType;
  entityId: string;
  entityName: string;
  // Display names of the metrics referenced by the entity (deduped, may be []).
  metricNames: string[];
  // The live on-screen state — dates, filters, gadget dimensions. Present only
  // when the renderer published one; both Reports and Dashboards do (see
  // RegisterReportInsightsView / RegisterDashboardInsightsView). Values are
  // merged in later, at send time.
  view?: InsightsView;
}

// A valid empty Dashboard used as the content fallback when nothing is being
// viewed, so useContentMetricIds can be called unconditionally (rules of hooks)
// and simply returns [].
const EMPTY_DASHBOARD: DashboardType = {
  version: '5',
  id: '',
  name: '',
  canvas: { cards: [] },
  isTemplate: false,
  variableDrillDowns: [],
  access: { version: '2', type: 'Private' },
  v3Access: { isPublic: false, userIds: [], groupIds: [] },
  isV3Published: false,
  labelIds: [],
  createdBy: '',
  createdOn: '',
  updatedBy: '',
  updatedOn: '',
};

const EMPTY_CONTENT: ContentInput = {
  contentType: 'Dashboard',
  content: EMPTY_DASHBOARD,
};

// The content the user is currently viewing, enriched with its metric names.
// Sourced from InsightsContentProvider, which the leaf Dashboard/Report
// components publish to — robust across V1/V3 routing, workspaces, apps and
// portals (no route parsing).
const useCurrentInsightsEntity = (): CurrentInsightsEntity | null => {
  const { current, currentView } = useContext(InsightsContentContext);
  const { metricOptionsLookup } = useMetricOptions();

  const metricIds = useContentMetricIds(current?.content ?? EMPTY_CONTENT);

  return useMemo(() => {
    if (!current) {
      return null;
    }
    const metricNames = metricIds
      .map((id) => metricOptionsLookup[id]?.name)
      .filter((name): name is string => Boolean(name));

    // Only attach the view when it belongs to THIS entity — the two are published
    // from different depths of the tree and can be momentarily out of step during
    // a navigation.
    const view =
      currentView && currentView.entityId === current.entityId
        ? currentView.view
        : undefined;

    return {
      entityType: current.entityType,
      entityId: current.entityId,
      entityName: current.entityName,
      metricNames: [...new Set(metricNames)],
      ...(view ? { view } : {}),
    };
  }, [current, currentView, metricIds, metricOptionsLookup]);
};

export default useCurrentInsightsEntity;
